import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, CircularProgress,
  Alert, Checkbox, Chip, Divider, Tooltip
} from '@mui/material';
import { Sparkles, X, AlertTriangle, Play, RefreshCw, Crosshair } from 'lucide-react';
import { API_BASE_URL } from '../utils/api';

export const DEFAULT_GEMINI_REVIEW_INSTRUCTION =
  'Проверь переводы с русского на английский на предмет потери смысла и качества. ' +
  'Например, если переведенная фраза имеет грубые ошибки, которых намеренно не было ' +
  'сделано в русском оригинале для стилизации реплик, или ты считаешь что какая-то ' +
  'английская фраза будет мозолить глаза нативщикам.';

const SEVERITY_CONFIG = {
  critical: { label: 'critical', color: '#f87171', bg: 'rgba(248, 113, 113, 0.16)' },
  major: { label: 'major', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.16)' },
  minor: { label: 'minor', color: '#a3a3a3', bg: 'rgba(163, 163, 163, 0.16)' }
};

function SeverityChip({ severity }) {
  const cfg = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.minor;
  return (
    <Chip
      size="small"
      label={cfg.label}
      sx={{
        height: 20,
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: cfg.color,
        bgcolor: cfg.bg,
        border: `1px solid ${cfg.color}33`,
        '& .MuiChip-label': { px: 0.75 }
      }}
    />
  );
}

function TextDiffBlock({ langLabel, before, after }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, color: 'text.disabled', minWidth: 18 }}>
          {langLabel}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: '0.8rem',
            color: after ? 'text.disabled' : 'text.primary',
            textDecoration: after ? 'line-through' : 'none',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          {before || <em style={{ opacity: 0.5 }}>(пусто)</em>}
        </Typography>
      </Box>
      {after !== undefined && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, pl: 2.5 }}>
          <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, color: '#34d399', minWidth: 8 }}>
            →
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: '0.8rem',
              color: '#bbf7d0',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {after}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function ProposalCard({ proposal, index, checked, onToggle, onFocusNode, skippedReason }) {
  const { nodeId, field, variantIndex, severity, ruBefore, enBefore, ruAfter, enAfter, explanation } = proposal;
  const fieldLabel = field === 'variant' ? `variant #${(variantIndex ?? 0) + 1}` : 'line';
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        p: 1.25,
        borderRadius: '8px',
        border: '1px solid rgba(255,255,255,0.08)',
        bgcolor: skippedReason ? 'rgba(248, 113, 113, 0.04)' : 'rgba(255,255,255,0.02)',
        opacity: skippedReason ? 0.7 : 1
      }}
    >
      <Checkbox
        size="small"
        checked={checked}
        onChange={onToggle}
        disabled={!!skippedReason}
        sx={{ p: 0.5, mt: -0.25 }}
      />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <SeverityChip severity={severity} />
          <Tooltip title="Focus node on canvas">
            <Chip
              size="small"
              clickable
              onClick={() => onFocusNode?.(nodeId)}
              icon={<Crosshair size={11} strokeWidth={2.25} />}
              label={nodeId}
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontFamily: 'monospace',
                bgcolor: 'rgba(139, 92, 246, 0.12)',
                color: '#c4b5fd',
                border: '1px solid rgba(139, 92, 246, 0.25)',
                '& .MuiChip-icon': { color: '#c4b5fd', ml: 0.5 },
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          </Tooltip>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>
            {fieldLabel}
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
            #{index + 1}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <TextDiffBlock langLabel="EN" before={enBefore} after={enAfter} />
          <TextDiffBlock langLabel="RU" before={ruBefore} after={ruAfter} />
        </Box>

        <Box sx={{
          display: 'flex', gap: 0.75, alignItems: 'flex-start',
          mt: 0.25, p: 0.75, borderRadius: '6px',
          bgcolor: 'rgba(139, 92, 246, 0.06)', border: '1px solid rgba(139, 92, 246, 0.12)'
        }}>
          <Sparkles size={12} strokeWidth={2.25} color="#a78bfa" style={{ marginTop: 2, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.75rem', color: '#ddd6fe', lineHeight: 1.4 }}>
            {explanation}
          </Typography>
        </Box>

        {skippedReason && (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.25 }}>
            <AlertTriangle size={12} color="#f87171" />
            <Typography sx={{ fontSize: '0.7rem', color: '#fecaca' }}>
              Skipped: {skippedReason}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function GeminiReviewDialog({
  open,
  onClose,
  buildGraphJson,
  applyProposals,
  storyflowId,
  teamId,
  getToken,
  onFocusNode
}) {
  const [instruction, setInstruction] = useState(DEFAULT_GEMINI_REVIEW_INSTRUCTION);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [proposals, setProposals] = useState(null); // null = idle/no run yet
  const [selected, setSelected] = useState(() => new Set());
  const [skippedById, setSkippedById] = useState({}); // proposalKey -> reason
  const [applyResult, setApplyResult] = useState(null);

  const reset = useCallback(() => {
    setProposals(null);
    setSelected(new Set());
    setSkippedById({});
    setApplyResult(null);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    if (loading) return;
    onClose?.();
  }, [loading, onClose]);

  const handleRun = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    setApplyResult(null);
    setSkippedById({});
    try {
      const token = await getToken();
      const graph = buildGraphJson();
      const res = await fetch(`${API_BASE_URL}/api/gemini/storyflow-review`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userInstruction: instruction,
          graph,
          teamId,
          storyflowId
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || data.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const list = Array.isArray(data.proposals) ? data.proposals : [];
      setProposals(list);
      setSelected(new Set(list.map((_, i) => i)));
    } catch (err) {
      setError(err.message || String(err));
      setProposals(null);
    } finally {
      setLoading(false);
    }
  }, [loading, getToken, buildGraphJson, instruction, teamId, storyflowId]);

  const toggleOne = useCallback((idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const setAll = useCallback((predicate) => {
    if (!proposals) return;
    const next = new Set();
    proposals.forEach((p, i) => { if (predicate(p, i)) next.add(i); });
    setSelected(next);
  }, [proposals]);

  const handleApply = useCallback(() => {
    if (!proposals || selected.size === 0) return;
    const accepted = proposals.filter((_, i) => selected.has(i));
    const result = applyProposals(accepted) || { applied: 0, skipped: [] };
    const skipMap = {};
    for (const s of result.skipped || []) {
      const idx = proposals.indexOf(s.proposal);
      if (idx >= 0) skipMap[idx] = s.reason;
    }
    setSkippedById(skipMap);
    setApplyResult({ applied: result.applied || 0, total: accepted.length, skipped: (result.skipped || []).length });
    setSelected((prev) => {
      const next = new Set();
      prev.forEach((i) => { if (skipMap[i]) next.add(i); });
      return next;
    });
  }, [proposals, selected, applyProposals]);

  const counts = useMemo(() => {
    if (!proposals) return null;
    const c = { critical: 0, major: 0, minor: 0 };
    proposals.forEach((p) => { if (c[p.severity] != null) c[p.severity] += 1; });
    return c;
  }, [proposals]);

  const showResults = proposals !== null;

  if (!open) return null;

  return (
    <Box
      sx={{
        width: 460,
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#0a0a0b',
        borderRight: '1px solid rgba(255,255,255,0.08)'
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, flexShrink: 0 }}>
        <Sparkles size={18} strokeWidth={2.25} color="#a78bfa" />
        <Typography sx={{ flex: 1, fontSize: '0.95rem', fontWeight: 600 }}>
          Gemini · sfDialogue translation review
        </Typography>
        <IconButton size="small" onClick={handleClose} disabled={loading}>
          <X size={16} />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

      {/* Body — scrollable */}
      <Box className="vr-scroll-y" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
            Prompt (editable; baked-in system rules are added on the server)
          </Typography>
          <TextField
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            disabled={loading}
            placeholder="What should Gemini check..."
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.02)',
                fontSize: '0.85rem',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' }
              }
            }}
          />
          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.5 }}>
            Server rules: sfDialogue only · ≤10 proposals · sorted by severity · explanations in Russian · Russian edits only when the prompt asks for them. Model: gemini-3-flash-preview.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ fontSize: '0.8rem' }}>
            {error}
          </Alert>
        )}

        {applyResult && (
          <Alert
            severity={applyResult.skipped > 0 ? 'warning' : 'success'}
            onClose={() => setApplyResult(null)}
            sx={{ fontSize: '0.8rem' }}
          >
            Applied {applyResult.applied} of {applyResult.total}.
            {applyResult.skipped > 0 && ` ${applyResult.skipped} skipped (see red cards below).`}
          </Alert>
        )}

        {showResults && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                Proposals: {proposals.length}
              </Typography>
              {counts && (counts.critical > 0 || counts.major > 0 || counts.minor > 0) && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {counts.critical > 0 && <SeverityChip severity="critical" />}
                  {counts.critical > 0 && (
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{counts.critical}</Typography>
                  )}
                  {counts.major > 0 && <SeverityChip severity="major" />}
                  {counts.major > 0 && (
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{counts.major}</Typography>
                  )}
                  {counts.minor > 0 && <SeverityChip severity="minor" />}
                  {counts.minor > 0 && (
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{counts.minor}</Typography>
                  )}
                </Box>
              )}
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={() => setAll(() => true)} disabled={proposals.length === 0}>
                All
              </Button>
              <Button size="small" onClick={() => setAll((p) => p.severity === 'critical')} disabled={!counts?.critical}>
                Critical only
              </Button>
              <Button size="small" onClick={() => setSelected(new Set())} disabled={selected.size === 0}>
                Clear
              </Button>
            </Box>

            {proposals.length === 0 ? (
              <Box sx={{
                py: 3, textAlign: 'center',
                color: 'text.secondary', fontSize: '0.85rem',
                border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '8px'
              }}>
                Gemini returned no proposals. Tweak the prompt and try again.
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {proposals.map((p, i) => (
                  <ProposalCard
                    key={`${p.nodeId}-${p.field}-${p.variantIndex ?? 'x'}-${i}`}
                    proposal={p}
                    index={i}
                    checked={selected.has(i)}
                    onToggle={() => toggleOne(i)}
                    onFocusNode={onFocusNode}
                    skippedReason={skippedById[i]}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

      {/* Footer — sticky */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, flexShrink: 0, flexWrap: 'wrap' }}>
        {showResults && (
          <Button size="small" onClick={reset} startIcon={<RefreshCw size={14} />} disabled={loading}>
            Reset
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={handleClose} disabled={loading}>
          Close
        </Button>
        <Button
          size="small"
          variant={showResults ? 'outlined' : 'contained'}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Play size={14} />}
          onClick={handleRun}
          disabled={loading || !instruction.trim()}
        >
          {showResults ? 'Run again' : 'Run'}
        </Button>
        {showResults && (
          <Button
            size="small"
            variant="contained"
            startIcon={<Sparkles size={14} />}
            onClick={handleApply}
            disabled={loading || selected.size === 0}
          >
            Apply ({selected.size})
          </Button>
        )}
      </Box>
    </Box>
  );
}
